import { useEffect, useState } from 'react';
import { can } from '../../../config/adminPermissions.js';
import { useScreenProbe } from '../../../utils/screenTiming.js';
import { hpApi } from './hpApi.js';
import { HpToastProvider, HpIcon } from './HpUi.jsx';
import HpPostsPanel from './HpPostsPanel.jsx';
import HpCompanyPanel from './HpCompanyPanel.jsx';
import HpPeoplePanel from './HpPeoplePanel.jsx';
import HpLegalPanel from './HpLegalPanel.jsx';
import './hp.css';

const TABS = [
  { id: 'posts', label: 'お知らせ記事' },
  { id: 'company', label: '会社情報' },
  { id: 'team', label: 'チーム' },
  { id: 'experts', label: '専門家・パートナー' },
  { id: 'legal', label: '法務' },
];

const EXPERT_SUBTABS = [
  { id: 'experts', label: '監修者・顧問' },
  { id: 'partners', label: '提携企業' },
];

export default function AdminHpManagement({ user, onOpenLegal }) {
  const probe = useScreenProbe('hp-management');
  const [tab, setTab] = useState('posts');
  const [expertSub, setExpertSub] = useState('experts');
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState('');
  const canWrite = can(user, 'homepage:write');

  useEffect(() => {
    let alive = true;
    probe.start('meta');
    hpApi.meta()
      .then((m) => { if (alive) setMeta(m); probe.end('meta', 'ok'); })
      .catch((err) => { if (alive) setMetaError(err.message || 'HP データに接続できませんでした'); probe.end('meta', 'error'); })
      .finally(() => probe.unlock());
    return () => { alive = false; };
  }, [probe]);

  const siteUrl = meta?.siteUrl || 'https://www.converge-p.co.jp';
  const isProdDb = meta?.database === '(default)';

  const onKeyDownTabs = (e) => {
    const i = TABS.findIndex((t) => t.id === tab);
    if (e.key === 'ArrowRight') setTab(TABS[(i + 1) % TABS.length].id);
    if (e.key === 'ArrowLeft') setTab(TABS[(i - 1 + TABS.length) % TABS.length].id);
  };

  return (
    <HpToastProvider>
      <div className="hp-root">
        <header className="hp-header">
          <div>
            <h2 className="hp-header__title">HP管理</h2>
            <p className="hp-header__desc">コーポレートサイトに表示する記事・会社情報・人物・法務文書を編集します。保存した内容はおよそ1分以内に HP に反映されます。</p>
          </div>
          <div className="hp-header__side">
            {meta && (
              <span className={`hp-env${isProdDb ? ' is-prod' : ''}`} title={`${meta.projectId} / ${meta.database}`}>
                {isProdDb ? '本番データ' : `検証データ（${meta.database}）`}
              </span>
            )}
            <a className="hp-btn hp-btn--ghost" href={siteUrl} target="_blank" rel="noreferrer"><HpIcon.External />HPを開く</a>
          </div>
        </header>

        {!canWrite && (
          <p className="hp-note"><HpIcon.Lock />閲覧のみの権限です。編集には「HP管理の編集・公開」権限が必要です。</p>
        )}
        {metaError && <p className="hp-note hp-note--error"><HpIcon.Alert />{metaError}</p>}

        <div className="hp-tabs" role="tablist" aria-label="HP管理" onKeyDown={onKeyDownTabs}>
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`hp-tab-${t.id}`}
              aria-selected={tab === t.id}
              aria-controls={`hp-panel-${t.id}`}
              tabIndex={tab === t.id ? 0 : -1}
              className={`hp-tabs__tab${tab === t.id ? ' is-active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div role="tabpanel" id={`hp-panel-${tab}`} aria-labelledby={`hp-tab-${tab}`} className="hp-tabpanel">
          {tab === 'posts' && <HpPostsPanel siteUrl={siteUrl} canWrite={canWrite} />}
          {tab === 'company' && <HpCompanyPanel canWrite={canWrite} />}
          {tab === 'team' && <HpPeoplePanel kind="team" siteUrl={siteUrl} canWrite={canWrite} />}
          {tab === 'experts' && (
            <>
              <div className="hp-segment" role="group" aria-label="表示する一覧">
                {EXPERT_SUBTABS.map((s) => (
                  <button key={s.id} type="button" className={`hp-segment__btn${expertSub === s.id ? ' is-active' : ''}`} aria-pressed={expertSub === s.id} onClick={() => setExpertSub(s.id)}>
                    {s.label}
                  </button>
                ))}
              </div>
              <HpPeoplePanel key={expertSub} kind={expertSub} siteUrl={siteUrl} canWrite={canWrite} />
            </>
          )}
          {tab === 'legal' && <HpLegalPanel siteUrl={siteUrl} canWrite={canWrite} onOpenLegal={onOpenLegal} />}
        </div>
      </div>
    </HpToastProvider>
  );
}
